import { Link } from "react-router-dom";
import { Wallet, ScanLine, LayoutDashboard, ArrowUpRight, ShieldCheck } from "lucide-react";

const products = [
  {
    title: "Worker Digital Wallet",
    desc: "Workers carry every license, certification and safety card in one verified wallet — with expiration alerts.",
    icon: Wallet,
    to: "/wallet",
    testid: "card-wallet",
    tag: "01 / Worker",
  },
  {
    title: "QR Job Site Verification",
    desc: "Foreman scans a worker QR code at the gate. One scan returns a clear Ready for Site, Action Required, or Not Eligible answer.",
    icon: ScanLine,
    to: "/verify/marcus-reed",
    testid: "card-qr",
    tag: "02 / Foreman",
  },
  {
    title: "Employer Compliance Dashboard",
    desc: "See workforce compliance, expiring credentials, and job-site readiness across every project, in real time.",
    icon: LayoutDashboard,
    to: "/dashboard",
    testid: "card-dashboard",
    tag: "03 / Employer",
  },
];

export default function Landing() {
  return (
    <div data-testid="landing-page" className="relative">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 procred-grid pointer-events-none" />
        <div className="relative mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-20 md:grid-cols-12 md:px-12 md:py-28">
          <div className="md:col-span-7">
            <div
              data-testid="hero-eyebrow"
              className="inline-flex items-center gap-2 border border-slate-200 bg-white/70 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-600"
            >
              <span className="h-1.5 w-1.5 bg-[#FF5A1F]" />
              ProCred / Workforce Compliance OS
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display mt-6 text-5xl font-black leading-[0.95] tracking-tighter text-[#0B1221] sm:text-6xl md:text-7xl"
            >
              The problem isn’t data.
              <br />
              <span className="text-[#FF5A1F]">It’s disconnection.</span>
            </h1>

            <p
              data-testid="hero-subheadline"
              className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg"
            >
              One platform for verified worker credentials, job-site clearance, and
              employer compliance. Built for skilled trades. Built for trust.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-3">
              <Link
                to="/wallet"
                data-testid="cta-view-wallet"
                className="group inline-flex items-center gap-2 bg-[#FF5A1F] px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-white transition-all hover:bg-[#e94d14] hover:-translate-y-0.5"
              >
                View Worker Wallet
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/dashboard"
                data-testid="cta-view-dashboard"
                className="group inline-flex items-center gap-2 bg-[#0B1221] px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-white transition-all hover:bg-[#1E293B] hover:-translate-y-0.5"
              >
                View Employer Dashboard
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/scan"
                data-testid="cta-view-verify"
                className="inline-flex items-center gap-2 border border-slate-300 bg-white px-6 py-3.5 text-sm font-semibold uppercase tracking-wider text-[#0B1221] transition-colors hover:border-[#0B1221]"
              >
                Try QR Scan
              </Link>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-slate-200 pt-6">
              <div className="flex items-center gap-2 text-sm text-slate-700">
                <ShieldCheck className="h-4 w-4 text-[#FF5A1F]" strokeWidth={2.25} />
                <span className="font-semibold">Verified Skills.</span>
                <span className="text-slate-500">Trusted Workforce.</span>
              </div>
              <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                OSHA · NFPA · Red Cross · State Licensure
              </div>
            </div>
          </div>

          <div className="relative md:col-span-5">
            <div className="relative h-full min-h-[360px] overflow-hidden border border-slate-200 bg-slate-50">
              <img
                src="https://customer-assets.emergentagent.com/job_trusted-trades-57/artifacts/jq6bcnub_ChatGPT%20Image%20Jun%2026%2C%202026%2C%2011_37_05%20PM.png"
                alt="Two construction workers in safety vests and hard hats overlooking an active commercial job site at sunset, with tower cranes and steel framing"
                className="absolute inset-0 h-full w-full object-cover [object-position:50%_72%]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Product cards */}
      <section className="border-b border-slate-200">
        <div className="mx-auto max-w-7xl px-6 py-20 md:px-12 md:py-24">
          <div className="mb-12 flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
            <div>
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                One platform · Three surfaces
              </div>
              <h2 className="font-display mt-3 text-3xl font-bold tracking-tight text-[#0B1221] sm:text-4xl">
                Built for workers, foremen, and employers.
              </h2>
            </div>
            <p className="max-w-md text-sm text-slate-600">
              Credentials live with the worker. Clearance answers live at the gate.
              Compliance lives on the dashboard. Same source of truth.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {products.map((p, i) => (
              <Link
                key={p.title}
                to={p.to}
                data-testid={p.testid}
                className="group relative flex flex-col border border-slate-200 bg-white p-7 transition-all hover:-translate-y-1 hover:shadow-lg"
              >
                <div className="flex items-center justify-between">
                  <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    {p.tag}
                  </span>
                  <ArrowUpRight className="h-4 w-4 text-slate-400 transition-colors group-hover:text-[#FF5A1F]" />
                </div>
                <div className="mt-8 flex h-12 w-12 items-center justify-center bg-[#0B1221] text-white transition-colors group-hover:bg-[#FF5A1F]">
                  <p.icon className="h-5 w-5" strokeWidth={2} />
                </div>
                <h3 className="font-display mt-6 text-xl font-bold tracking-tight text-[#0B1221]">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                <div className="mt-6 border-t border-slate-100 pt-4 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                  Open module →
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer band */}
      <section className="bg-[#0B1221] text-white">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-6 py-12 md:flex-row md:items-center md:px-12">
          <div>
            <div className="font-display text-2xl font-bold tracking-tight">ProCred</div>
            <div className="mt-1 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-400">
              Verified Skills · Trusted Workforce
            </div>
          </div>
          <div className="text-sm text-slate-400">
            ProCred MVP · Verified Skills. Trusted Workforce.
          </div>
        </div>
      </section>
    </div>
  );
}
